// Copy for the AI Wallet: the restaurant section of the billing page and the
// Finance/admin tab. Kept in one place so both languages stay in step.

const en = {
  // ------------------------------------------------------------ restaurant
  sectionTitle: "AI Wallet",
  sectionNote: "Prepaid credit for AI features (AI Menu Import, Menu Copilot). It is separate from your subscription.",
  balance: "Available balance",
  reserved: "Reserved for operations in progress",
  addFunds: "Add funds",
  minTopUp: "Minimum top-up: {amount}",
  lowBalance: "Low balance",
  frozen: "Frozen",
  frozenNote: "This wallet is frozen. Contact LUD support.",
  notCharging: "AI usage is not deducted from this balance yet.",
  loading: "Loading wallet…",
  retry: "Retry",
  empty: "Nothing yet",

  openTopUpTitle: "Top-up in progress",
  credit: "Credit",
  toPay: "To pay",
  invoice: "Invoice",
  expires: "Expires",
  uploadReceipt: "Upload payment receipt",
  cancelRequest: "Cancel request",
  cancelConfirm: "Cancel this top-up request? Its invoice will be voided.",
  states: { awaiting_payment: "Waiting for payment", finance_review: "Under finance review", credited: "Credited", cancelled: "Cancelled", expired: "Expired" },
  stateNotes: {
    awaiting_payment: "Transfer the amount and upload the receipt. The balance is added after Finance approves the payment.",
    finance_review: "Your receipt was received. The balance is added once Finance approves it.",
  },

  usageTitle: "Recent AI usage",
  usageEmpty: "No AI usage has been charged yet.",
  transactionsTitle: "Wallet transactions",
  transactionsEmpty: "No transactions yet. Add funds to start.",
  topUpsTitle: "Top-up history",
  colDate: "Date",
  colOperation: "Operation",
  colBy: "By",
  colAmount: "Amount",
  colBalanceAfter: "Balance after",
  colType: "Type",
  colStatus: "Status",
  refunded: "Refunded {amount}",
  features: { menu_import: "AI Menu Import", menu_copilot: "Menu Copilot" },
  entryTypes: { TOP_UP: "Top-up", AI_USAGE: "AI usage", REFUND: "Refund", ADJUSTMENT: "Adjustment", REVERSAL: "Reversal" },
  showMore: "Show more",

  topUpModalTitle: "Add funds to the AI Wallet",
  topUpModalNote: "An invoice is created for this amount. Your balance is added after Finance approves the payment.",
  amountLabel: "Amount to add (SAR)",
  vat: "VAT ({rate}%)",
  totalToPay: "Total to pay",
  createRequest: "Create top-up invoice",
  creating: "Creating…",
  cancel: "Cancel",
  belowMinimum: "The minimum top-up is {amount}.",
  aboveMaximum: "The maximum top-up is {amount}.",
  invalidAmount: "Enter an amount in SAR with at most two decimals.",

  // -------------------------------------------------------- finance / admin
  tabLabel: "AI Wallets",
  admin: {
    title: "AI Wallets",
    description: "Prepaid AI credit per restaurant: balances, top-ups, the ledger and the cost of AI usage.",
    denied: "AI Wallet administration is not available for this account.",
    views: { wallets: "Wallets", topUps: "Top-ups", charges: "Usage & cost", rollout: "Rollout analysis", reconciliation: "Reconciliation", pricing: "Pricing & policy" },
    rollout: {
      note: "What charging AI usage would mean (shadow) or means (enforced), per operation. Use it to set holds and prices before enforcing. Internal only.",
      period: "Period",
      periods: { 7: "Last 7 days", 30: "Last 30 days", 90: "Last 90 days", all: "All time" },
      currentMode: "Current charging mode",
      operations: "Operations",
      openCharges: "Operations not yet settled",
      refused: "Refused for balance",
      truncated: "Only the most recent 20,000 operations are included.",
      empty: "No operations in this period and mode yet.",
      success: "Delivered",
      billable: "Billable",
      avgCost: "Avg. provider cost",
      tailCost: "Provider cost p95",
      avgCharge: "Avg. customer charge",
      exceedingHold: "Charges above the hold",
      hold: "Hold",
      maxOver: "Largest excess",
      insufficientWallet: "Would fail for balance",
      insufficientNote: "Operations the wallet could not have started at the time",
      unusedHold: "Avg. unused hold",
      absorbed: "Avg. cost absorbed by LUD",
      failedCost: "Failed operations",
      margin: "Margin",
      charged: "Charge",
      reliability: "Used a fallback model",
      retries: "With retries",
      requests: "Requests / operation",
      operationType: "Operation type",
      holdSizing: "Hold sizing",
      holdSizingNote: "In enforce a hold also caps what the operation may spend at the provider. Size holds by total provider spend (every request: retries, re-asks, fallback models, enrichment), not by the customer charge alone. The suggested hold is information only: create and activate a new pricing version to change holds.",
      pricingVersion: "Pricing version",
      versionOption: (v) => `v${v.version}${v.active ? " (active)" : ""}${v.provisional ? " (provisional)" : ""} · ${v.operations}`,
      allVersions: "All versions (charges mixed)",
      noVersions: "No data",
      activeVersionNote: (version) => `Active pricing version: v${version}`,
      customerCharge: "Customer charge",
      providerSpend: "Total provider spend",
      providerBudget: "Provider budget",
      overBudget: "Spend over budget",
      overHold: "Charge over hold",
      suggestedHold: "Suggested hold",
      suggestedNote: (s) => `1.5 × p99 spend ($${s.targetBudgetUsd}) at v${s.pricing.version}${s.pricing.active ? " (active)" : ""}`,
      notEnoughData: (s) => `Not enough data (${s.sampleSize} of ${s.minSampleSize} operations)`,
      indicative: "indicative only",
      noPricingData: "No pricing to convert with",
      unknownCost: (n) => `${n} with unknown cost (spend understated)`,
      avg: "avg",
    },
    unsettled: "Operations whose charge is not settled",
    unsettledNote: "The settlement failed or never ran. The scheduler finishes these from the saved result; the AI operation is never run again.",
    mode: "Charging mode",
    modes: { off: "Off", shadow: "Shadow (computed, not charged)", enforce: "Enforced" },
    search: "Search restaurant",
    lowBalanceOnly: "Low balance only",
    openWallet: "Open wallet",
    storeIdPlaceholder: "Restaurant ID",
    back: "Back to wallets",
    colRestaurant: "Restaurant",
    colBalance: "Balance",
    colReserved: "Reserved",
    colUpdated: "Updated",
    colKey: "Reference",
    colReason: "Reason",
    colActor: "By",
    colProviderCost: "Provider cost",
    colCustomerCharge: "Customer charge",
    colComputed: "Computed",
    colDebited: "Debited",
    colRefunded: "Refunded",
    colShortfall: "Shortfall",
    colMargin: "Margin",
    colMode: "Mode",
    colBillable: "Billable",
    colFeature: "Feature",
    colRequests: "Requests",
    colModel: "Model",
    colCost: "Cost (USD)",
    colTokens: "Tokens",
    ledgerOk: "Ledger matches balance",
    ledgerMismatch: "Ledger does not match the balance",
    ledger: "Ledger",
    holds: "Open holds",
    noWallets: "No wallets yet. A wallet is created the first time a restaurant uses it.",
    adjust: "Adjust balance",
    freeze: "Freeze",
    unfreeze: "Unfreeze",
    reverse: "Reverse",
    refund: "Refund",
    view: "View",
    direction: "Direction",
    directions: { credit: "Credit (add)", debit: "Debit (remove)" },
    amountSar: "Amount (SAR)",
    reason: "Reason (required)",
    allowPartial: "If the balance is lower, take what is available",
    confirm: "Confirm",
    reviewPayment: "Review payment",
    stateFilter: "State",
    all: "All",
    yes: "Yes",
    no: "No",
    entryStatus: { pending: "Pending", posted: "Posted", void: "Void" },
    summaryProviderCost: "Provider cost",
    summaryCharge: "Customer charge",
    summaryMargin: "Margin",
    summaryShadow: "Computed charge (before caps and refunds)",
    chargeDetail: "AI charge",
    pricingSnapshot: "Pricing used",
    rate: "USD → SAR rate",
    markup: "Markup (×)",
    fixedFee: "Fixed fee (SAR)",
    minCharge: "Minimum charge (SAR)",
    chargeBasis: "Charge basis",
    bases: { succeeded_attempts: "Successful requests only", all_reported_attempts: "Every billed request" },
    unknownCosts: "Provider requests with unknown cost",
    unknownCostsNote: "These requests got no response, so the provider may or may not have billed them. They are never charged to the customer.",
    shortfalls: "Charges the wallet could not fully cover",
    unposted: "Ledger entries not yet posted",
    resolveBilled: "Mark billed",
    resolveUnbilled: "Mark not billed",
    confirmedCost: "Confirmed cost (USD)",
    note: "Note",
    nothing: "Nothing to review.",
    pricingRules: "Pricing rules",
    pricingNote: "A price change is a new version. Existing charges keep the version they were priced with.",
    version: "Version",
    provisional: "Provisional",
    activate: "Activate",
    newRule: "New pricing version",
    holdsTitle: "Holds (SAR reserved before an operation)",
    holdLabels: { copilot_message: "Copilot message", copilot_regenerate: "Copilot regenerate", import_text: "Import: spreadsheet / text", import_image: "Import: image", import_pdf: "Import: scanned PDF" },
    createRule: "Create draft",
    policyTitle: "Top-up policy",
    minTopUpSar: "Minimum top-up (SAR)",
    maxTopUpSar: "Maximum top-up (SAR)",
    expiryDays: "Unpaid top-up expires after (days)",
    lowBalanceSar: "Low-balance threshold (SAR)",
    save: "Save",
    saved: "Saved.",
    ruleStatus: { draft: "Draft", active: "Active", archived: "Archived" },
  },
};

const ar = {
  sectionTitle: "محفظة الذكاء الاصطناعي",
  sectionNote: "رصيد مدفوع مسبقًا لخدمات الذكاء الاصطناعي (استيراد القائمة، مساعد القائمة). وهو مستقل عن اشتراكك.",
  balance: "الرصيد المتاح",
  reserved: "محجوز لعمليات جارية",
  addFunds: "إضافة رصيد",
  minTopUp: "الحد الأدنى للشحن: {amount}",
  lowBalance: "رصيد منخفض",
  frozen: "مجمّدة",
  frozenNote: "هذه المحفظة مجمّدة. تواصل مع دعم LUD.",
  notCharging: "لا يُخصم استخدام الذكاء الاصطناعي من هذا الرصيد حاليًا.",
  loading: "جارٍ تحميل المحفظة…",
  retry: "إعادة المحاولة",
  empty: "لا يوجد شيء بعد",

  openTopUpTitle: "طلب شحن قيد التنفيذ",
  credit: "الرصيد",
  toPay: "المبلغ المطلوب",
  invoice: "الفاتورة",
  expires: "ينتهي في",
  uploadReceipt: "رفع إيصال الدفع",
  cancelRequest: "إلغاء الطلب",
  cancelConfirm: "هل تريد إلغاء طلب الشحن؟ سيتم إلغاء فاتورته.",
  states: { awaiting_payment: "بانتظار الدفع", finance_review: "قيد مراجعة المالية", credited: "تمت الإضافة", cancelled: "ملغي", expired: "منتهي" },
  stateNotes: {
    awaiting_payment: "حوّل المبلغ وارفع الإيصال. يُضاف الرصيد بعد اعتماد الدفعة من قسم المالية.",
    finance_review: "تم استلام الإيصال. يُضاف الرصيد بعد اعتماده من قسم المالية.",
  },

  usageTitle: "أحدث استخدام للذكاء الاصطناعي",
  usageEmpty: "لم يُخصم أي استخدام حتى الآن.",
  transactionsTitle: "حركات المحفظة",
  transactionsEmpty: "لا توجد حركات بعد. أضف رصيدًا للبدء.",
  topUpsTitle: "سجل الشحن",
  colDate: "التاريخ",
  colOperation: "العملية",
  colBy: "بواسطة",
  colAmount: "المبلغ",
  colBalanceAfter: "الرصيد بعدها",
  colType: "النوع",
  colStatus: "الحالة",
  refunded: "مُسترد {amount}",
  features: { menu_import: "استيراد القائمة بالذكاء الاصطناعي", menu_copilot: "مساعد القائمة" },
  entryTypes: { TOP_UP: "شحن رصيد", AI_USAGE: "استخدام الذكاء الاصطناعي", REFUND: "استرداد", ADJUSTMENT: "تسوية", REVERSAL: "عكس قيد" },
  showMore: "عرض المزيد",

  topUpModalTitle: "إضافة رصيد لمحفظة الذكاء الاصطناعي",
  topUpModalNote: "تُنشأ فاتورة بهذا المبلغ، ويُضاف الرصيد بعد اعتماد الدفعة من قسم المالية.",
  amountLabel: "المبلغ المراد إضافته (ر.س)",
  vat: "ضريبة القيمة المضافة ({rate}%)",
  totalToPay: "الإجمالي المطلوب دفعه",
  createRequest: "إنشاء فاتورة الشحن",
  creating: "جارٍ الإنشاء…",
  cancel: "إلغاء",
  belowMinimum: "الحد الأدنى للشحن هو {amount}.",
  aboveMaximum: "الحد الأقصى للشحن هو {amount}.",
  invalidAmount: "أدخل مبلغًا بالريال بخانتين عشريتين كحد أقصى.",

  tabLabel: "محافظ الذكاء الاصطناعي",
  admin: {
    title: "محافظ الذكاء الاصطناعي",
    description: "رصيد الذكاء الاصطناعي المدفوع مسبقًا لكل مطعم: الأرصدة، الشحن، دفتر القيود وتكلفة الاستخدام.",
    denied: "إدارة محافظ الذكاء الاصطناعي غير متاحة لهذا الحساب.",
    views: { wallets: "المحافظ", topUps: "طلبات الشحن", charges: "الاستخدام والتكلفة", rollout: "تحليل التفعيل", reconciliation: "المطابقة", pricing: "التسعير والسياسة" },
    rollout: {
      note: "ما الذي يعنيه خصم استخدام الذكاء الاصطناعي لكل عملية: تجريبياً (يُحسب ولا يُخصم) أو فعلياً. يُستخدم لضبط مبالغ الحجز والأسعار قبل التفعيل. للاستخدام الداخلي فقط.",
      period: "الفترة",
      periods: { 7: "آخر 7 أيام", 30: "آخر 30 يوماً", 90: "آخر 90 يوماً", all: "كل الفترات" },
      currentMode: "وضع الخصم الحالي",
      operations: "العمليات",
      openCharges: "عمليات لم تُسوَّ بعد",
      refused: "رُفضت لعدم كفاية الرصيد",
      truncated: "يشمل التحليل آخر 20,000 عملية فقط.",
      empty: "لا توجد عمليات في هذه الفترة وهذا الوضع.",
      success: "مكتملة",
      billable: "قابلة للخصم",
      avgCost: "متوسط تكلفة المزوّد",
      tailCost: "تكلفة المزوّد p95",
      avgCharge: "متوسط المبلغ على العميل",
      exceedingHold: "مبالغ تجاوزت الحجز",
      hold: "الحجز",
      maxOver: "أكبر تجاوز",
      insufficientWallet: "كانت ستُرفض لعدم كفاية الرصيد",
      insufficientNote: "عمليات لم يكن رصيد المحفظة يكفي لبدئها وقتها",
      unusedHold: "متوسط الحجز غير المستخدم",
      absorbed: "متوسط التكلفة التي تتحملها لُد",
      failedCost: "العمليات الفاشلة",
      margin: "الهامش",
      charged: "المبلغ",
      reliability: "استُخدم النموذج البديل",
      retries: "مع إعادة محاولة",
      requests: "طلبات / عملية",
      operationType: "نوع العملية",
      holdSizing: "ضبط مبالغ الحجز",
      holdSizingNote: "عند التفعيل يحدّ الحجز أيضاً مما يمكن أن تنفقه العملية لدى المزوّد. اضبط الحجز وفق إجمالي إنفاق المزوّد (كل الطلبات: إعادة المحاولة وإعادة الطلب والنموذج البديل والإثراء)، لا وفق المبلغ على العميل وحده. الحجز المقترح للاطلاع فقط: لتغيير الحجز أنشئ نسخة تسعير جديدة وفعّلها.",
      pricingVersion: "نسخة التسعير",
      versionOption: (v) => `v${v.version}${v.active ? " (المفعّلة)" : ""}${v.provisional ? " (مؤقتة)" : ""} · ${v.operations}`,
      allVersions: "كل النسخ (مبالغ مختلطة)",
      noVersions: "لا توجد بيانات",
      activeVersionNote: (version) => `نسخة التسعير المفعّلة: v${version}`,
      customerCharge: "المبلغ على العميل",
      providerSpend: "إجمالي إنفاق المزوّد",
      providerBudget: "ميزانية المزوّد",
      overBudget: "إنفاق تجاوز الميزانية",
      overHold: "مبلغ تجاوز الحجز",
      suggestedHold: "الحجز المقترح",
      suggestedNote: (s) => `1.5 × إنفاق p99 ($${s.targetBudgetUsd}) وفق v${s.pricing.version}${s.pricing.active ? " (المفعّلة)" : ""}`,
      notEnoughData: (s) => `بيانات غير كافية (${s.sampleSize} من ${s.minSampleSize} عملية)`,
      indicative: "تقديري فقط",
      noPricingData: "لا يوجد تسعير للتحويل",
      unknownCost: (n) => `${n} بتكلفة غير معروفة (الإنفاق أقل من الحقيقي)`,
      avg: "متوسط",
    },
    unsettled: "عمليات لم يُسوَّ مبلغها",
    unsettledNote: "فشلت التسوية أو لم تُنفَّذ. يُكملها النظام تلقائياً من النتيجة المحفوظة، ولا تُعاد عملية الذكاء الاصطناعي أبداً.",
    mode: "وضع الخصم",
    modes: { off: "متوقف", shadow: "تجريبي (يُحسب ولا يُخصم)", enforce: "مفعّل" },
    search: "بحث عن مطعم",
    lowBalanceOnly: "الرصيد المنخفض فقط",
    openWallet: "فتح المحفظة",
    storeIdPlaceholder: "معرّف المطعم",
    back: "العودة إلى المحافظ",
    colRestaurant: "المطعم",
    colBalance: "الرصيد",
    colReserved: "المحجوز",
    colUpdated: "آخر تحديث",
    colKey: "المرجع",
    colReason: "السبب",
    colActor: "بواسطة",
    colProviderCost: "تكلفة المزوّد",
    colCustomerCharge: "المبلغ على العميل",
    colComputed: "المحسوب",
    colDebited: "المخصوم",
    colRefunded: "المُسترد",
    colShortfall: "العجز",
    colMargin: "الهامش",
    colMode: "الوضع",
    colBillable: "قابل للخصم",
    colFeature: "الخدمة",
    colRequests: "الطلبات",
    colModel: "النموذج",
    colCost: "التكلفة (دولار)",
    colTokens: "الرموز",
    ledgerOk: "دفتر القيود مطابق للرصيد",
    ledgerMismatch: "دفتر القيود لا يطابق الرصيد",
    ledger: "دفتر القيود",
    holds: "الحجوزات المفتوحة",
    noWallets: "لا توجد محافظ بعد. تُنشأ المحفظة عند أول استخدام للمطعم.",
    adjust: "تسوية الرصيد",
    freeze: "تجميد",
    unfreeze: "إلغاء التجميد",
    reverse: "عكس",
    refund: "استرداد",
    view: "عرض",
    direction: "الاتجاه",
    directions: { credit: "إضافة", debit: "خصم" },
    amountSar: "المبلغ (ر.س)",
    reason: "السبب (إلزامي)",
    allowPartial: "إذا كان الرصيد أقل، يُخصم المتاح فقط",
    confirm: "تأكيد",
    reviewPayment: "مراجعة الدفعة",
    stateFilter: "الحالة",
    all: "الكل",
    yes: "نعم",
    no: "لا",
    entryStatus: { pending: "معلّق", posted: "مُرحّل", void: "ملغي" },
    summaryProviderCost: "تكلفة المزوّد",
    summaryCharge: "المبلغ على العملاء",
    summaryMargin: "الهامش",
    summaryShadow: "المبلغ المحسوب (قبل الحدود والاستردادات)",
    chargeDetail: "عملية ذكاء اصطناعي",
    pricingSnapshot: "التسعير المستخدم",
    rate: "سعر صرف الدولار إلى الريال",
    markup: "معامل الربح (×)",
    fixedFee: "رسم ثابت (ر.س)",
    minCharge: "الحد الأدنى للخصم (ر.س)",
    chargeBasis: "أساس الاحتساب",
    bases: { succeeded_attempts: "الطلبات الناجحة فقط", all_reported_attempts: "كل طلب محتسب" },
    unknownCosts: "طلبات للمزوّد بتكلفة غير معروفة",
    unknownCostsNote: "لم تصل استجابة لهذه الطلبات، فقد يكون المزوّد احتسبها أو لا. لا تُخصم من العميل أبدًا.",
    shortfalls: "عمليات لم تغطِّها المحفظة بالكامل",
    unposted: "قيود لم تُرحّل بعد",
    resolveBilled: "تم احتسابها",
    resolveUnbilled: "لم تُحتسب",
    confirmedCost: "التكلفة المؤكدة (دولار)",
    note: "ملاحظة",
    nothing: "لا يوجد ما يحتاج مراجعة.",
    pricingRules: "قواعد التسعير",
    pricingNote: "تغيير السعر يُنشئ إصدارًا جديدًا. العمليات السابقة تحتفظ بالإصدار الذي سُعّرت به.",
    version: "الإصدار",
    provisional: "مؤقت",
    activate: "تفعيل",
    newRule: "إصدار تسعير جديد",
    holdsTitle: "الحجوزات (المبلغ المحجوز قبل العملية)",
    holdLabels: { copilot_message: "رسالة مساعد القائمة", copilot_regenerate: "إعادة توليد المساعد", import_text: "استيراد: جدول / نص", import_image: "استيراد: صورة", import_pdf: "استيراد: PDF ممسوح" },
    createRule: "إنشاء مسودة",
    policyTitle: "سياسة الشحن",
    minTopUpSar: "الحد الأدنى للشحن (ر.س)",
    maxTopUpSar: "الحد الأقصى للشحن (ر.س)",
    expiryDays: "ينتهي طلب الشحن غير المدفوع بعد (أيام)",
    lowBalanceSar: "حد الرصيد المنخفض (ر.س)",
    save: "حفظ",
    saved: "تم الحفظ.",
    ruleStatus: { draft: "مسودة", active: "مفعّل", archived: "مؤرشف" },
  },
};

export function useAiWalletText(lang = "ar") {
  return lang === "en" ? en : ar;
}

export const fill = (template, values = {}) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, value), template || "");

export const formatWalletDate = (value, lang = "ar") => (value ? new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "ar-SA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value)) : "-");

export const STATE_BADGE = { awaiting_payment: "badge-warning", finance_review: "badge-warning", credited: "badge-success", cancelled: "badge-neutral", expired: "badge-neutral" };
